import { useContext, useState, useCallback, useEffect, useRef } from 'react';
import { ChatContext } from '../context/ChatContext';
import { chatUnreadAPI } from '../services/chatUnreadService';

/**
 * Custom hook to access the chat context
 * @returns {Object} Chat context values and methods
 */
const useChat = () => {
  const context = useContext(ChatContext);
  
  if (!context) {
    throw new Error('useChat must be used within a ChatProvider');
  }

  // Enhanced unread count state management
  const [realTimeUnreadCount, setRealTimeUnreadCount] = useState(0);
  const [isLoadingUnreadCount, setIsLoadingUnreadCount] = useState(false);
  const [conversationUnreadCounts, setConversationUnreadCounts] = useState({});
  
  // Refs for debouncing and caching
  const lastUpdateTime = useRef(0);
  const updateTimeoutRef = useRef(null);

  // Optimized fetch unread count with caching
  const fetchUnreadCount = useCallback(async (forceRefresh = false) => {
    if (!context.isAuth) return;
    
    // Return cached value immediately if not forcing refresh and cache is fresh
    if (!forceRefresh) {
      const cached = chatUnreadAPI.getCachedUnreadCount();
      if (cached !== null) {
        setRealTimeUnreadCount(cached);
        return cached;
      }
    }
    
    try {
      setIsLoadingUnreadCount(true);
      const response = await chatUnreadAPI.getUnreadCount();
      
      if (response?.data?.unreadCount !== undefined) {
        const newCount = response.data.unreadCount;
        setRealTimeUnreadCount(newCount);
        
        // Update cache
        chatUnreadAPI.updateUnreadCountCache(newCount);
        
        return newCount;
      }
    } catch (error) {
      console.error('Error fetching unread count:', error);
      // Fallback to context unread count
      const fallbackCount = context.unreadCount || 0;
      setRealTimeUnreadCount(fallbackCount);
      return fallbackCount;
    } finally {
      setIsLoadingUnreadCount(false);
    }
  }, [context.isAuth, context.unreadCount]);

  // Instant unread count update (called by socket events)
  const updateUnreadCountInstantly = useCallback((newCount, conversationId = null, increment = false) => {
    const now = Date.now();
    lastUpdateTime.current = now;
    
    // Update cache immediately
    const updatedCount = chatUnreadAPI.updateUnreadCountCache(newCount, conversationId, increment);
    
    // Update state immediately
    setRealTimeUnreadCount(updatedCount);
    
    // Update conversation-specific counts
    if (conversationId) {
      setConversationUnreadCounts(prev => ({
        ...prev,
        [conversationId]: increment 
          ? (prev[conversationId] || 0) + (newCount || 1)
          : Math.max(0, newCount || 0)
      }));
    }
    
    // Debounced API sync (to avoid spam)
    if (updateTimeoutRef.current) {
      clearTimeout(updateTimeoutRef.current);
    }
    
    updateTimeoutRef.current = setTimeout(() => {
      // Only sync if this is still the latest update
      if (lastUpdateTime.current === now) {
        fetchUnreadCount(true); // Force refresh from server
      }
    }, 2000); // Sync with server after 2 seconds of no updates
    
    return updatedCount;
  }, [fetchUnreadCount]);

  // Enhanced mark conversation as read with instant updates
  const markConversationAsRead = useCallback(async (userId) => {
    if (!userId) return;
    
    try {
      // Instant UI update (optimistic)
      const conversationId = [userId, context.user?.data?.user?._id || 'current'].sort().join('_');
      const conversationCount = conversationUnreadCounts[conversationId] || 0;
      
      if (conversationCount > 0) {
        updateUnreadCountInstantly(-conversationCount, conversationId, true);
      }
      
      // API call (non-blocking for UI)
      await chatUnreadAPI.markAllAsRead(userId);
      
      // Refresh conversations to sync
      if (context.fetchConversations) {
        context.fetchConversations();
      }
      
    } catch (error) {
      console.error('Error marking conversation as read:', error);
      // Revert optimistic update on error
      fetchUnreadCount(true);
    }
  }, [context.user, context.fetchConversations, conversationUnreadCounts, updateUnreadCountInstantly]);

  // Enhanced mark single message as read
  const markMessageAsRead = useCallback(async (messageId) => {
    if (!messageId) return;
    
    try {
      // Instant UI update (optimistic)
      updateUnreadCountInstantly(-1, null, true);
      
      // API call (non-blocking for UI)
      await chatUnreadAPI.markMessageAsRead(messageId);
      
    } catch (error) {
      console.error('Error marking message as read:', error);
      // Revert optimistic update on error
      fetchUnreadCount(true);
    }
  }, [updateUnreadCountInstantly, fetchUnreadCount]);

  // Enhanced WebSocket event handlers
  useEffect(() => {
    if (!context.isAuth || !context.socket) return;

    const socket = context.socket;
    console.log('[useChat] Setting up enhanced socket listeners');

    // Main unread count update event (CRITICAL for real-time)
    const handleUnreadCountUpdate = (data) => {
      console.log('[useChat] Unread count updated:', data);
      
      if (data?.unreadCount !== undefined) {
        updateUnreadCountInstantly(data.unreadCount, data.conversationId, false);
      }
      
      // Handle conversation-specific updates
      if (data?.conversationId && data?.lastMessage) {
        setConversationUnreadCounts(prev => ({
          ...prev,
          [data.conversationId]: {
            count: data.unreadCount || 0,
            lastMessage: data.lastMessage,
            timestamp: Date.now()
          }
        }));
      }
    };

    // Initial unread count response
    const handleUnreadCount = (data) => {
      console.log('[useChat] Initial unread count:', data);
      if (data?.unreadCount !== undefined) {
        updateUnreadCountInstantly(data.unreadCount, null, false);
      }
    };

    // New message received (increment count)
    const handleMessageReceived = (message) => {
      console.log('[useChat] New message received:', message);
      
      // Only increment if not in active conversation
      const isActiveConversation = context.activeConversation?.recipient?._id === message.sender._id;
      
      if (!isActiveConversation) {
        const conversationId = [message.sender._id, message.recipient._id].sort().join('_');
        updateUnreadCountInstantly(1, conversationId, true);
      }
    };

    // Message sent (no count change, but update conversations)
    const handleMessageSent = (data) => {
      console.log('[useChat] Message sent:', data);
      // Refresh conversations to show latest message
      if (context.fetchConversations) {
        setTimeout(() => context.fetchConversations(), 100);
      }
    };

    // Conversation marked as read
    const handleMessagesMarkedRead = (data) => {
      console.log('[useChat] Messages marked as read:', data);
      
      if (data?.conversationId) {
        // Reset conversation count to 0
        updateUnreadCountInstantly(0, data.conversationId, false);
      }
    };

    // Socket event listeners
    socket.on('message:unread-count-updated', handleUnreadCountUpdate);
    socket.on('message:unread-count', handleUnreadCount);
    socket.on('message:received', handleMessageReceived);
    socket.on('message:sent', handleMessageSent);
    socket.on('messages:marked-read', handleMessagesMarkedRead);

    // Request initial unread count
    socket.emit('message:get-unread-count');

    // Cleanup
    return () => {
      console.log('[useChat] Cleaning up socket listeners');
      socket.off('message:unread-count-updated', handleUnreadCountUpdate);
      socket.off('message:unread-count', handleUnreadCount);
      socket.off('message:received', handleMessageReceived);
      socket.off('message:sent', handleMessageSent);
      socket.off('messages:marked-read', handleMessagesMarkedRead);
      
      if (updateTimeoutRef.current) {
        clearTimeout(updateTimeoutRef.current);
      }
    };
  }, [context.isAuth, context.socket, context.activeConversation, context.fetchConversations, updateUnreadCountInstantly]);

  // Initial fetch and auth state changes
  useEffect(() => {
    if (context.isAuth) {
      // Immediate fetch on auth
      fetchUnreadCount(false);
      
      // Set up periodic sync (every 30 seconds as backup)
      const interval = setInterval(() => {
        fetchUnreadCount(true);
      }, 30000);
      
      return () => clearInterval(interval);
    } else {
      // Clear on logout
      setRealTimeUnreadCount(0);
      setConversationUnreadCounts({});
      chatUnreadAPI.clearCache();
    }
  }, [context.isAuth, fetchUnreadCount]);

  // Auto-mark as read when entering conversation
  useEffect(() => {
    if (context.activeConversation?.recipient?._id) {
      const userId = context.activeConversation.recipient._id;
      const conversationId = [userId, context.user?.data?.user?._id || 'current'].sort().join('_');
      
      // Only mark as read if there are unread messages
      if (conversationUnreadCounts[conversationId] > 0) {
        setTimeout(() => markConversationAsRead(userId), 500); // Small delay to ensure UI is ready
      }
    }
  }, [context.activeConversation?.recipient?._id, conversationUnreadCounts, markConversationAsRead]);

  return {
    ...context,
    // Enhanced unread count (prioritize real-time, fallback to context)
    unreadCount: realTimeUnreadCount || context.unreadCount || 0,
    realTimeUnreadCount,
    conversationUnreadCounts,
    isLoadingUnreadCount,
    
    // Enhanced methods
    fetchUnreadCount,
    markConversationAsRead,
    markMessageAsRead,
    updateUnreadCountInstantly,
    
    // Utility methods
    getCachedUnreadCount: chatUnreadAPI.getCachedUnreadCount,
    clearUnreadCache: chatUnreadAPI.clearCache
  };
};

export default useChat;